/*
  ========================================================================
  THEME: Impact v7.0.1 merge target + Gro unified rebuild
  STORES: Gro Clinics AU (groclinics.com.au) + NZ (groclinics.co.nz)
  LAST UPDATED: 25/08/2026
  PREVIOUSLY: 15/07/2026
  ========================================================================
  gro-video.js - shared, mobile-first video harness. Defines the custom
  element <gro-video> that snippets/gro-video.liquid emits, so no Gro
  section needs its own inline video script.

  ------------------------------------------------------------------------
  OWNERSHIP
  ------------------------------------------------------------------------
  OWNS   Player mounting, source selection, autoplay policy, the
         single-player-at-a-time registry and focus handling.
  DOES NOT OWN  The markup (snippets/gro-video.liquid), the player surface
         styling (assets/gro-video.css), section chrome or width, or
         article body token parsing (module - gro-article).

  ------------------------------------------------------------------------
  GRO MODULE CONTRACT - read before editing
  ------------------------------------------------------------------------
  BLAST RADIUS  Loaded from theme.liquid on every page of both stores and
    consumed by sixteen files. There is no per-section copy. Treat every
    change as a storefront-wide change.
  DOM HOOKS  <gro-video>, .gro-video__activator, .gro-video__poster,
    .gro-video__play, script.gro-video__sources. The sources script is both
    the JSON payload and the mount anchor (insertBefore targets it), so it
    must remain the last child.
  RECONNECT SAFETY  The theme editor moves the SAME node on section
    reorder, firing disconnect then connect. One-time work is guarded by
    _init; listeners, the IntersectionObserver and registry membership
    re-attach on every connect. Anything added to connectedCallback must be
    idempotent or _init-guarded.
  AUTOPLAY LATCH  A rejected muted autoplay sets this.autoplay = false
    permanently and reveals the play affordance. Without that latch the
    element re-mounts on every viewport re-entry (iOS Low Power Mode).
  PAUSE INTENT  _systemPause marks scroll-out and single-play pauses so
    they are not recorded as _userPaused. Losing that distinction stops the
    observer ever resuming an ambient clip.
  ACCESSIBILITY  playsinline is always set. Native controls stay on except
    for looping ambient clips, which keep a transparent pause toggle
    (WCAG 2.2.2). Focus is moved onto the mounted player and back to the
    activator on restore, never left on <body>.
  DOCTRINE  Modern JS only (const/let, arrow functions, optional chaining),
    CSS over JS, no em dashes. Log every edit in the module CHANGELOG.md
    and bump LAST UPDATED.

  ------------------------------------------------------------------------
  DEPENDENCIES
  ------------------------------------------------------------------------
  snippets/gro-video.liquid for the markup contract above, and
  assets/gro-video.css for the facade, ambient and blocked-autoplay states
  (.gro-video--ambient, .gro-video--show-play, .is-playing are set here and
  styled there). No libraries. No metaobjects, metafields or global events.

  ------------------------------------------------------------------------
  DELIBERATE EXEMPTIONS
  ------------------------------------------------------------------------
  None. The marked HLS.js hook in selectSource is an unwired extension
  point, not an exemption.

  ------------------------------------------------------------------------
  Mobile-first behaviour:
   - Poster facade: no <video> in the DOM until activation, so the poster
     stays the LCP candidate and no video bytes load up front.
   - playsinline is always set (stops iOS forcing fullscreen).
   - Adaptive source: native HLS (.m3u8) where supported (Safari and iOS
     get true adaptive bitrate). Other browsers get the mp4 rendition
     closest to the rendered size and connection, so phones do not pull a
     1080p file. See selectProgressiveSource for the HLS.js extension hook.
   - Autoplay only when muted, in view, not reduced-motion, not Save-Data.
     Pauses when scrolled out of view to save battery and data.
   - One video plays at a time across the page.
   - Theme-editor safe: connected/disconnected callbacks handle re-render.
  ========================================================================
*/

(() => {
  if (window.customElements && customElements.get('gro-video')) return;

  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection || null;

  const prefersLightData = () => {
    if (!connection) return false;
    if (connection.saveData) return true;
    return /(^|-)2g$/.test(connection.effectiveType || '');
  };

  // Page-wide registry so only one player runs at a time. Ambient clips
  // (muted background loops) neither claim the slot nor get paused by it.
  const registry = new Set();
  const pauseOthers = (active) => {
    registry.forEach((instance) => {
      if (instance !== active && !instance.isAmbient) instance.pauseMedia();
    });
  };

  class GroVideo extends HTMLElement {
    connectedCallback() {
      // One-time setup, then per-connect wiring: the theme editor moves the
      // SAME node on section reorder (disconnect + reconnect), so listeners,
      // observer and registry must re-attach on every connect.
      if (!this._init) {
        this._init = true;
        this.config = this.readConfig();
        this.activator = this.querySelector('.gro-video__activator');
        this.media = null;
        this.frameLoaded = false;
        this.io = null;
        this.onActivate = this.onActivate.bind(this);
        this._activatorLabel = this.activator ? this.activator.getAttribute('aria-label') : '';

        const isProvider = this.config && (this.config.type === 'youtube' || this.config.type === 'vimeo' || this.config.type === 'tolstoy');
        // Ambient clips (muted background loops) opt out of the single-play rules.
        this.isAmbient = this.dataset.autoplay === 'true';
        this.autoplay = this.isAmbient && !isProvider && !reducedMotion.matches && !prefersLightData();
      }
      if (!this.config) return;

      if (this.activator) this.activator.addEventListener('click', this.onActivate);
      this.setupObserver();
      registry.add(this);
    }

    disconnectedCallback() {
      registry.delete(this);
      if (this.io) { this.io.disconnect(); this.io = null; }
      if (this.activator && this.onActivate) this.activator.removeEventListener('click', this.onActivate);
      this.pauseMedia();
    }

    readConfig() {
      const node = this.querySelector('.gro-video__sources');
      if (!node) return null;
      try {
        return JSON.parse(node.textContent);
      } catch (err) {
        return null;
      }
    }

    setupObserver() {
      if (!('IntersectionObserver' in window)) {
        if (this.autoplay) this.activate(true);
        return;
      }
      this.io = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            if (this.media) {
              // Never resume over a deliberate user pause.
              if (this.autoplay && this.media.paused && !this._userPaused) this.media.play().catch(() => {});
            } else if (this.autoplay) {
              this.activate(true);
            }
          } else if (this.media && !this.media.paused) {
            this.pauseMedia();
          }
        });
      }, { threshold: 0.25, rootMargin: '200px 0px' });
      this.io.observe(this);
    }

    onActivate(event) {
      event.preventDefault();
      if (this.media) {
        this.media.paused ? this.media.play() : this.media.pause();
        return;
      }
      this.activate(false);
    }

    activate(isAuto) {
      if (this.media || this.frameLoaded) return;
      const type = this.config.type;
      if (type === 'youtube' || type === 'vimeo' || type === 'tolstoy') {
        this.mountIframe(type);
        return;
      }
      this.mountVideo(isAuto);
    }

    mountIframe(type) {
      const src = this.embedUrl(type, this.config.src);
      if (!src) return;
      const iframe = document.createElement('iframe');
      iframe.src = src;
      iframe.setAttribute('allow', 'autoplay; fullscreen; picture-in-picture; clipboard-write');
      iframe.setAttribute('allowfullscreen', '');
      iframe.setAttribute('title', this.getAttribute('data-title') || 'Video');
      this.replaceActivator(iframe);
      this.frameLoaded = true;
      // Hiding the focused activator would drop focus to body.
      iframe.focus({ preventScroll: true });
    }

    mountVideo(isAuto) {
      const video = document.createElement('video');
      video.playsInline = true;
      video.setAttribute('playsinline', '');
      video.preload = 'metadata';

      const posterImg = this.querySelector('.gro-video__poster');
      const posterSrc = posterImg ? (posterImg.currentSrc || posterImg.src) : '';
      if (posterSrc) video.poster = posterSrc;

      video.loop = this.dataset.loop === 'true';
      // Autoplay is only allowed muted, so force mute on the auto path.
      video.muted = isAuto ? true : this.dataset.muted === 'true';
      // Native controls give scrubbing, fullscreen and captions on mobile,
      // which beats a tap-only overlay. Off only for looping AMBIENT clips;
      // a tap-to-play loop must keep controls or it cannot be paused at all.
      video.controls = this.dataset.controls !== 'false' && !(video.loop && isAuto);

      const source = this.selectSource(video);
      if (!source) return;
      video.src = source;

      const title = this.getAttribute('data-title');
      if (title) video.setAttribute('aria-label', title);

      this.bindMediaEvents(video);
      if (isAuto) {
        // Ambient mount: keep the activator as a transparent pause/play toggle
        // over the clip (WCAG 2.2.2 - looping media must be pausable).
        this.classList.add('gro-video--ambient');
        if (this.activator) this.activator.setAttribute('aria-label', 'Pause video');
        this.insertBefore(video, this.querySelector('.gro-video__sources'));
      } else {
        this.replaceActivator(video);
        // The activator button hides on mount; keep keyboard/AT focus on the player.
        video.focus({ preventScroll: true });
      }
      this.media = video;

      const playback = video.play();
      if (playback && typeof playback.catch === 'function') {
        playback.catch((err) => {
          if (isAuto) {
            // Blocked muted autoplay (e.g. iOS Low Power Mode) would otherwise
            // re-mount on every viewport re-entry, so stop auto attempts for
            // good and reveal the play affordance the autoplay styling hides.
            this.autoplay = false;
            this.classList.remove('gro-video--ambient');
            if (this.activator && this._activatorLabel) this.activator.setAttribute('aria-label', this._activatorLabel);
            this.restoreActivator();
            this.classList.add('gro-video--show-play');
            return;
          }
          // User-initiated: only restore the facade if the browser refused the
          // gesture outright. On other rejections (AbortError races, power
          // saving) keep the mounted player so its native controls still work.
          if (err && err.name === 'NotAllowedError') {
            this.restoreActivator();
            // Removing the focused video would strand focus on body.
            if (this.activator) this.activator.focus({ preventScroll: true });
          }
        });
      }
    }

    selectSource(video) {
      const config = this.config;
      if (config.type !== 'file') return config.src || '';

      const sources = Array.isArray(config.sources) ? config.sources : [];
      if (!sources.length) return '';

      const hls = sources.find((s) => (s.type || '').indexOf('mpegurl') !== -1 || (s.url || '').indexOf('.m3u8') !== -1);
      const progressive = sources.filter((s) => s !== hls && s.url);

      // Safari and iOS play HLS natively and adapt bitrate to the network.
      if (hls && video.canPlayType('application/vnd.apple.mpegurl')) return hls.url;

      const picked = this.selectProgressiveSource(progressive);
      if (picked) return picked;

      // HLS.js hook: load hls.js and attach it to hls.url here to give
      // non-Safari browsers true adaptive bitrate. Native HLS plus the
      // right-sized mp4 below cover the common cases without the dependency.
      return hls ? hls.url : '';
    }

    selectProgressiveSource(progressive) {
      if (!progressive.length) return '';

      const sorted = progressive
        .map((s) => ({ url: s.url, width: Number(s.width) || 0 }))
        .sort((a, b) => a.width - b.width);

      // Cap DPR at 2: past that the bitrate cost outweighs the gain on video.
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      let target = (this.clientWidth || window.innerWidth || 360) * dpr;

      const eff = connection && connection.effectiveType ? connection.effectiveType : '';
      if (connection && connection.saveData) target = Math.min(target, 480);
      else if (/(^|-)2g$/.test(eff)) target = Math.min(target, 480);
      else if (eff === '3g') target = Math.min(target, 720);

      const fit = sorted.find((s) => s.width >= target);
      return (fit || sorted[sorted.length - 1]).url;
    }

    bindMediaEvents(video) {
      video.addEventListener('play', () => {
        this._userPaused = false;
        if (!this.isAmbient) pauseOthers(this);
        if (this.isAmbient && this.activator) this.activator.setAttribute('aria-label', 'Pause video');
        this.classList.add('is-playing');
      });
      video.addEventListener('pause', () => {
        this.classList.remove('is-playing');
        if (this.isAmbient && this.activator) this.activator.setAttribute('aria-label', this._activatorLabel || 'Play video');
        // Pauses we did not trigger (scroll-out, single-play) are the user's.
        if (this._systemPause) this._systemPause = false;
        else this._userPaused = true;
      });
    }

    replaceActivator(node) {
      if (this.activator) {
        this.activator.hidden = true;
        this.activator.setAttribute('aria-hidden', 'true');
      }
      this.insertBefore(node, this.querySelector('.gro-video__sources'));
    }

    restoreActivator() {
      if (this.media) {
        this.media.remove();
        this.media = null;
      }
      if (this.activator) {
        this.activator.hidden = false;
        this.activator.removeAttribute('aria-hidden');
      }
    }

    pauseMedia() {
      if (this.media && !this.media.paused) {
        this._systemPause = true;
        this.media.pause();
      }
    }

    embedUrl(type, raw) {
      if (!raw) return '';
      if (type === 'youtube') {
        const match = raw.match(/(?:youtu\.be\/|v=|embed\/|shorts\/|live\/)([\w-]+)/);
        return match ? 'https://www.youtube.com/embed/' + match[1] + '?autoplay=1&rel=0&playsinline=1' : '';
      }
      if (type === 'vimeo') {
        const match = raw.match(/vimeo\.com\/(?:video\/)?(\d+)/);
        return match ? 'https://player.vimeo.com/video/' + match[1] + '?autoplay=1' : '';
      }
      // Tolstoy player URL is used as-is.
      return raw;
    }
  }

  customElements.define('gro-video', GroVideo);
})();
